'use client'

import Link from 'next/link'
import { Warning, Trash } from '@phosphor-icons/react'
import { Check } from '@/components/icons/glyphs'
import { cn } from '@/lib/utils'

export type MutationKind = 'create' | 'update' | 'delete' | 'complete'

/**
 * Compact confirmation strip for write tools. Deliberately not a full card —
 * a create/update is a status line, not a result set, and stacking cards for
 * every mutation in a bulk turn would bury the assistant's actual reply.
 */
export function MutationResult({
    success,
    message,
    kind,
    href,
    linkLabel = 'View',
}: {
    success: boolean
    message: string
    kind: MutationKind
    href?: string
    linkLabel?: string
}) {
    const failed = !success
    const deleted = !failed && kind === 'delete'
    const Icon = failed ? Warning : deleted ? Trash : Check

    return (
        <div
            className={cn(
                'flex items-center gap-3 rounded-lg border px-4 py-3 shadow-sm',
                failed && 'border-destructive/20 bg-destructive/12 text-destructive',
                // A successful delete is not a celebration. Sage would read as
                // "great!" over a destructive, cascading action; Terracotta would
                // read as an error. Neutral states the fact and moves on.
                deleted && 'border-border bg-muted text-muted-foreground',
                !failed && !deleted && 'border-success/20 bg-success/12 text-success'
            )}
        >
            <Icon className="size-4 shrink-0" />
            <p className="min-w-0 flex-1 text-sm">{message}</p>
            {!failed && href && (
                <Link
                    href={href}
                    className="shrink-0 text-xs font-medium underline underline-offset-2 hover:no-underline"
                >
                    {linkLabel}
                </Link>
            )}
        </div>
    )
}
